/**
 * @fileoverview "Start from CMS" button + dialog for the Basics step.
 *
 * If no matter exists yet, the dialog collects the CMS URL, validates the
 * creation form, implicitly creates the matter, then starts the workflow.
 * If the matter already exists, it just starts the workflow.
 */
import { Globe, Loader2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useStartCaseIngestionWorkflowMutation } from '@/services/api/caseIngestionService';
import { getApiErrorMessage } from '@/utils/api-error';

import { useUnifiedFlow } from '../context/UnifiedFlowContext';

export const StartFromCmsButton: React.FC = () => {
  const {
    state,
    creationForm,
    handleCreateMatter,
  } = useUnifiedFlow();
  const { matterId, isCreatingMatter } = state;

  // Watch required fields to disable button until form is valid
  const firm = creationForm.watch('firm');
  const firstName = creationForm.watch('first_name');
  const lastName = creationForm.watch('last_name');
  const matterType = creationForm.watch('matter_type');
  const hasRequiredFields = !!(firm?.id && firstName?.trim() && lastName?.trim() && matterType);

  const [open, setOpen] = useState(false);
  const [cmsUrl, setCmsUrl] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [startWorkflow] = useStartCaseIngestionWorkflowMutation();

  const handleStart = useCallback(async () => {
    if (!cmsUrl.trim()) {
      toast.error('Please enter a CMS URL');
      return;
    }

    setIsStarting(true);
    try {
      let resolvedMatterId = matterId;

      // If matter doesn't exist, validate form and create it first
      if (!resolvedMatterId) {
        // Trigger form validation
        const isValid = await creationForm.trigger();
        if (!isValid) {
          toast.error('Please fill in the required fields (Firm, Name, Case Type)');
          setIsStarting(false);
          return;
        }

        const { matterId: newMatterId } = await handleCreateMatter();
        resolvedMatterId = newMatterId;
      }

      // Start the ingestion workflow
      await startWorkflow({
        matterId: resolvedMatterId,
        data: { external_cms_url: cmsUrl.trim() },
      }).unwrap();

      toast.success('CMS ingestion started');
      setOpen(false);
      setCmsUrl('');
    } catch (err: unknown) {
      const message = getApiErrorMessage(err) ?? 'Failed to start ingestion';
      toast.error(message);
    } finally {
      setIsStarting(false);
    }
  }, [cmsUrl, matterId, creationForm, handleCreateMatter, startWorkflow]);

  const isLoading = isStarting || isCreatingMatter;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          disabled={!matterId && !hasRequiredFields}
        >
          <Globe className="h-3.5 w-3.5" />
          Start from CMS
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Start CMS Ingestion</DialogTitle>
          <DialogDescription>
            Paste the Filevine project URL. A browser agent will scrape the case
            data and populate the intake fields automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="cms-url-input" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Filevine Project URL
            </label>
            <input
              id="cms-url-input"
              type="url"
              placeholder="https://firm.filevineapp.com/#/project/12345"
              value={cmsUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCmsUrl(e.target.value)}
              disabled={isLoading}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={isLoading || !cmsUrl.trim()}>
            {isLoading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                {!matterId ? 'Creating & Starting…' : 'Starting…'}
              </>
            ) : (
              'Start Ingestion'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
